import { styled } from '@/src/stitches.config'
import HeadlineEyebrow from '@/src/common/components/marketing/headlines/HeadlineEyebrow'
import {
    LoginProvider,
    useLoginContext,
} from '@/src/modules/login/LoginContext'
import { useEffect } from 'react'
import { EXISTING_LOGIN } from '@/src/modules/login/LoginConstants'
import { useUser } from '@/src/modules/auth/context/AuthProvider'
import { useRouter } from 'next/router'
import { ClipLoader } from 'react-spinners'
import { LoadingWrapper } from '@/src/modules/pg-plus/PGPlusStyledComponents'
import { StyledH1 } from '@/src/common/components/elements/StyledComponents'
import LoginFormWithCustomLeftContent from '@/src/common/components/elements/Forms/LoginFormWithCustomLeftContent'
import { GradientText } from '@/src/common/components/marketing/heros/StyledComponents'
import Image from 'next/image'
import { REFER_A_FRIEND_PAGE } from '@/src/modules/pg-plus/constants'
import {
    REFER_IMAGE_FIST_BUMP,
    REFER_IMAGE_GIRLS,
    REFER_IMAGE_GROUP,
} from '@/src/common/components/marketing/heros/constants'
import getBuilderPageContent from '@/src/common/queries/getBuilderPageContent'
import getBuilderBannersOrPromos from '@/src/common/queries/getBuilderBannersOrPromos'
import { BuilderComponent } from '@builder.io/react'
import { InferGetStaticPropsType } from 'next'
import '@/src/builderCustomComponentImports'

const BoldH1 = styled(StyledH1, {
    textTransform: 'uppercase',
    fontWeight: 900,
    fontSize: '2.15rem',
    '@sm': {
        fontSize: '2.85rem',
    },
})

const StyledBodyText = styled('p', {
    fontSize: '1.4rem',
    fontWeight: 400,
    color: '$dark',
})

const ImageWrapper = styled('div', {
    width: '100%',
    '& img': {
        display: 'block',
        width: '100%',
        height: 'auto',
        objectFit: 'cover',
        br: 20,
    },
    variants: {
        display: {
            'hide-on-mobile': {
                display: 'none',
                '@sm': {
                    display: 'block',
                },
            },
        },
        size: {
            xs: {
                '& img': {
                    width: 64,
                    height: 64,
                },
            },
            sm: {
                '& img': {
                    width: 90,
                    height: 90,
                },
            },
            md: {
                '& img': {
                    width: 124,
                    height: 124,
                },
            },
        },
    },
})

const ReferralLoginPage = () => {
    const {
        loginType,
        actionText,
        setPhoneVerificationText,
        sentFrom,
        isAdaChat,
    } = useLoginContext()

    const { isLoggedIn, isLoaded, isVerified } = useUser()
    const router = useRouter()

    // pushes the user back to the referral page upon login
    useEffect(() => {
        if (isLoggedIn && isVerified) {
            router.replace(REFER_A_FRIEND_PAGE).then()
        }
    }, [isAdaChat, isLoggedIn, isVerified, router, sentFrom])

    // CTA button text when entering phone verification code
    // needs to be different here in the login page
    useEffect(() => {
        setPhoneVerificationText(actionText)
    }, [actionText, setPhoneVerificationText])

    if (!isLoaded) {
        return (
            <LoadingWrapper>
                <ClipLoader size={50} data-testid={'loader'} />
            </LoadingWrapper>
        )
    }

    return (
        <LoginFormWithCustomLeftContent>
            <ImageWrapper size={'sm'} display={'hide-on-mobile'}>
                <Image
                    src={REFER_IMAGE_GIRLS}
                    alt={'girls-with-dog'}
                    height={377}
                    width={593}
                    style={{ marginLeft: 'auto' }}
                />
            </ImageWrapper>
            <HeadlineEyebrow
                label={
                    loginType === EXISTING_LOGIN
                        ? 'Welcome Back!'
                        : 'Get Started'
                }
                style={{ marginTop: 15 }}
            />
            <BoldH1>
                Refer a friend <br />
                <GradientText>You both earn $5</GradientText>
            </BoldH1>
            <StyledBodyText>
                Enter the email associated with your PerfectGift account where
                we can send your reward.
            </StyledBodyText>
            <ImageWrapper size={'xs'}>
                <Image
                    src={REFER_IMAGE_FIST_BUMP}
                    alt={'fist-bump'}
                    height={97}
                    width={97}
                    style={{ marginBottom: 10 }}
                />
            </ImageWrapper>
            <ImageWrapper size={'md'}>
                <Image
                    src={REFER_IMAGE_GROUP}
                    alt={'group-of-friends'}
                    height={564}
                    width={816}
                    style={{ marginLeft: '55%' }}
                />
            </ImageWrapper>
        </LoginFormWithCustomLeftContent>
    )
}

export default function Page({
    content,
}: {
    content: InferGetStaticPropsType<any>
}) {
    return (
        <LoginProvider>
            <ReferralLoginPage />
            <BuilderComponent model="page" content={content} />
        </LoginProvider>
    )
}

export async function getStaticProps() {
    // remove /test once ready for prod -> and in builder
    const { content } = await getBuilderPageContent({
        params: {
            page: ['test/login/refer-a-friend'],
        },
    })

    const { consumerData: consumerBanner } = await getBuilderBannersOrPromos(
        true
    )

    return {
        props: {
            content,
            consumerBanner,
            data: {
                title: 'Login to Refer a Friend',
                description:
                    'Log into your PerfectGift account to begin receiving rewards for referring your friends, family, and more.',
            },
        },
        revalidate: 30,
    }
}
